import React, { useState, useEffect } from "react";
import styled from "styled-components";
import moment from "moment";
import { getLocale } from "react-i18nify";
import { useSnackbar } from "notistack";
import * as MatStyled from "@mui/material/styles";

import { getSellerProfile, updateSellerProfile } from "src/api/seller";

import { device } from "src/styles/device";
import { decryptData } from "src/components/profile/DecryptData";
import { checkNotEmpty, downloadImage } from "src/common";

import { Grid, Divider } from "@mui/material";
import TextField from "@mui/material/TextField";

import { Delete, PhotoCameraOutlined } from "@mui/icons-material";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";

import ModalFullImage from "src/components/ui/ModalFullImage";
import StyledButton from "src/components/core/Button";
import DropZone from "src/components/core/DropZone";
import { useDialog } from "src/context/DialogContext";
import AddLocationDialog from "../seller/AddLocationDialog";

/* ─── Layout ─────────────────────────────────────────────── */

const drawerWidth = 260;

const Main = MatStyled.styled("main", {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  position: "relative",
  flexGrow: 1,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
    position: "relative",
  }),
}));

const PageContainer = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: auto;
  padding: 32px 40px 64px;

  @media ${device.tabletL} {
    padding: 24px 20px 48px;
  }
`;

/* ─── Page Header ─────────────────────────────────────────── */

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #252525;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: "";
    display: inline-block;
    width: 4px;
    height: 28px;
    background: #e13b30;
    border-radius: 3px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
`;

/* ─── Cards ───────────────────────────────────────────────── */

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #ebebed;
  border-radius: 20px;
  padding: 28px 32px;
  margin-bottom: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

  @media ${device.tabletL} {
    padding: 20px;
  }
`;

const CardTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #252525;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: "";
    display: inline-block;
    width: 3px;
    height: 18px;
    background: #e13b30;
    border-radius: 2px;
  }
`;

/* ─── Stat Cards ──────────────────────────────────────────── */

const StatCard = styled.div`
  background: #ffffff;
  border: 1px solid #ebebed;
  border-radius: 16px;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  margin-bottom: 16px;
`;

const StatIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #fdf0ef;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StatLabel = styled.p`
  font-size: 0.75rem;
  color: #6c6c70;
  margin: 0 0 4px;
`;

const StatValue = styled.p`
  font-size: 1rem;
  font-weight: 600;
  color: #252525;
  margin: 0;
`;

/* ─── Form Fields ─────────────────────────────────────────── */

const FieldGroup = styled.div`
  margin-bottom: 20px;
`;

const FieldLabel = styled.p`
  font-size: 0.75rem;
  font-weight: 500;
  color: #6c6c70;
  margin: 0 0 6px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const FieldValue = styled.p`
  font-size: 0.9375rem;
  color: #252525;
  margin: 0;
  word-break: break-word;
  line-height: 1.55;
`;

const FieldValueDesc = styled.p`
  font-size: 0.9375rem;
  color: #252525;
  margin: 0;
  word-break: break-word;
  line-height: 1.55;
  max-height: 130px;
  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 5px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: #d8d8dc;
    border-radius: 100px;
  }
`;

const WrapperTextfield = styled.div`
  width: 100%;

  .MuiOutlinedInput-input {
    background: #fff;
    border-radius: 12px;
    padding: 10px 14px;
  }
  .MuiOutlinedInput-root {
    border-radius: 12px;
  }
  .MuiTextField-root {
    border-radius: 12px;
  }
  .MuiFormHelperText-root {
    color: #e13b30;
  }
`;

/* ─── Image Upload / Preview ──────────────────────────────── */

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
`;

const ImageItem = styled.div`
  background: #f8f8f9;
  border: 1px dashed #d8d8dc;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
`;

const ImageItemInner = styled.div`
  aspect-ratio: 4/3;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ImageItemWideInner = styled.div`
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media ${device.tabletL} {
    height: 140px;
  }

  @media ${device.mobile} {
    height: 100px;
  }
`;

const ImageMeta = styled.div`
  padding: 10px 14px 12px;
`;

const ImageMetaLabel = styled.p`
  font-size: 0.7rem;
  font-weight: 500;
  color: #6c6c70;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin: 0 0 2px;
`;

const ImageMetaDesc = styled.p`
  font-size: 0.8125rem;
  color: #252525;
  margin: 0;
  line-height: 1.4;
`;

const DeleteBtn = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  transition: background 0.15s;

  &:hover {
    background: #fdf0ef;
  }

  svg {
    font-size: 1rem;
    color: #e04132;
  }
`;

const UploadPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
  padding: 12px;

  svg {
    color: #bcbcc0;
    font-size: 1.75rem;
  }

  span {
    font-size: 0.75rem;
    color: #6c6c70;
  }

  &:hover svg {
    color: #e13b30;
  }
`;

const FileTip = styled.span`
  font-size: 0.625rem;
  color: #e13b30;
  text-align: center;
  margin-top: 2px;
  line-height: 1.4;
`;

const ViewFullLink = styled.span`
  display: block;
  text-align: center;
  font-size: 0.7rem;
  color: #6c6c70;
  padding: 4px 0 8px;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: #e13b30;
  }
`;

const PdfIconWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 6px;
  padding: 16px;
`;

const DownloadPdfLink = styled.span`
  font-size: 0.75rem;
  color: #252525;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: #e13b30;
  }
`;

/* ─── Helper: image src ───────────────────────────────────── */
const imgSrc = (value) =>
  typeof value === "string" ? value : URL.createObjectURL(value);

/* ─── Sub-components ──────────────────────────────────────── */

const ImageUploadCard = ({
  value,
  onDrop,
  onDelete,
  accept,
  label,
  description,
  width,
  height,
  wide = false,
}) => {
  const hasImage = checkNotEmpty(value);
  const Inner = wide ? ImageItemWideInner : ImageItemInner;

  return (
    <ImageItem style={wide ? { width: "100%" } : {}}>
      {hasImage ? (
        <>
          <Inner>
            <img src={imgSrc(value)} alt={label} />
            <DeleteBtn onClick={onDelete}>
              <Delete />
            </DeleteBtn>
          </Inner>
        </>
      ) : (
        <DropZone onDrop={onDrop} accept={accept}>
          {(props) => (
            <Inner style={{ minHeight: wide ? undefined : "140px" }}>
              <UploadPlaceholder {...props}>
                <PhotoCameraOutlined />
                <span>อัพโหลดรูปภาพ</span>
                <FileTip>.jpg .jpeg .png .webp</FileTip>
              </UploadPlaceholder>
            </Inner>
          )}
        </DropZone>
      )}
      <ImageMeta>
        <ImageMetaLabel>{label}</ImageMetaLabel>
        <ImageMetaDesc>{description}</ImageMetaDesc>
      </ImageMeta>
    </ImageItem>
  );
};

const ImageViewCard = ({ value, label, description, wide = false, onView }) => {
  const hasImage = checkNotEmpty(value);
  const Inner = wide ? ImageItemWideInner : ImageItemInner;

  return (
    <ImageItem style={wide ? { width: "100%" } : {}}>
      <Inner
        style={
          !hasImage
            ? { background: "#f8f8f9", minHeight: wide ? undefined : "140px" }
            : {}
        }
      >
        {hasImage ? (
          <img
            src={typeof value === "string" ? value : imgSrc(value)}
            alt={label}
          />
        ) : (
          <InsertPhotoIcon sx={{ color: "#d8d8dc", fontSize: "2.5rem" }} />
        )}
      </Inner>
      {hasImage && onView && typeof value === "string" && (
        <ViewFullLink onClick={onView}>
          คลิกเพิ่มเติมเพื่อดูรูปเต็ม
        </ViewFullLink>
      )}
      <ImageMeta>
        <ImageMetaLabel>{label}</ImageMetaLabel>
        <ImageMetaDesc>{description}</ImageMetaDesc>
      </ImageMeta>
    </ImageItem>
  );
};

// const PdfUploadCard = ({ value, onDrop, onDelete, label, description }) => {
//   const hasPdf = checkNotEmpty(value);
//   return (
//     <ImageItem>
//       {hasPdf ? (
//         <>
//           <ImageItemInner style={{ minHeight: "140px", background: "#f8f8f9" }}>
//             <PdfIconWrap>
//               <TextSnippetIcon sx={{ fontSize: "2.5rem", color: "#e13b30" }} />
//             </PdfIconWrap>
//             <DeleteBtn onClick={onDelete}>
//               <Delete />
//             </DeleteBtn>
//           </ImageItemInner>
//         </>
//       ) : (
//         <DropZone onDrop={onDrop} accept=".pdf">
//           {(props) => (
//             <ImageItemInner style={{ minHeight: "140px" }}>
//               <UploadPlaceholder {...props}>
//                 <PhotoCameraOutlined />
//                 <span>อัพโหลด PDF</span>
//               </UploadPlaceholder>
//             </ImageItemInner>
//           )}
//         </DropZone>
//       )}
//       <ImageMeta>
//         <ImageMetaLabel>{label}</ImageMetaLabel>
//         <ImageMetaDesc>{description}</ImageMetaDesc>
//       </ImageMeta>
//     </ImageItem>
//   );
// };

// const PdfViewCard = ({ value, label, description, onDownload }) => {
//   const hasPdf = checkNotEmpty(value);
//   return (
//     <ImageItem>
//       <ImageItemInner style={{ minHeight: "140px", background: "#f8f8f9" }}>
//         {hasPdf ? (
//           <PdfIconWrap>
//             <TextSnippetIcon sx={{ fontSize: "2.5rem", color: "#e13b30" }} />
//             <DownloadPdfLink onClick={onDownload}>Download PDF</DownloadPdfLink>
//           </PdfIconWrap>
//         ) : (
//           <InsertPhotoIcon sx={{ color: "#d8d8dc", fontSize: "2.5rem" }} />
//         )}
//       </ImageItemInner>
//       <ImageMeta>
//         <ImageMetaLabel>{label}</ImageMetaLabel>
//         <ImageMetaDesc>{description}</ImageMetaDesc>
//       </ImageMeta>
//     </ImageItem>
//   );
// };

/* ─── Mixed Upload / View (Image + PDF, max 4 MB) ─────────── */

const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4 MB

const isPdfValue = (value) => {
  if (!value) return false;
  if (typeof value === "string") return value.toLowerCase().includes(".pdf");
  if (value instanceof File)
    return (
      value.type === "application/pdf" ||
      value.name?.toLowerCase().endsWith(".pdf")
    );
  return false;
};

const transformPdfUrl = (url) => {
  if (typeof url !== "string") return url;
  return url
    .replace(
      /devimage\.xsurface\.com/,
      "devx-test-image.s3-ap-southeast-1.amazonaws.com",
    )
    .replace(
      /productimage\.xsurface\.com/,
      "product-xsurface.s3.ap-southeast-1.amazonaws.com",
    );
};

const MixedUploadCard = ({
  value,
  onDrop,
  onDelete,
  label,
  description,
  onSizeError,
}) => {
  const hasValue = checkNotEmpty(value);
  const isPdf = isPdfValue(value);

  const handleDrop = (files) => {
    const file = files[0];
    if (file && file.size > MAX_FILE_SIZE) {
      onSizeError && onSizeError();
      return;
    }
    onDrop(files);
  };

  return (
    <ImageItem>
      {hasValue ? (
        <>
          <ImageItemInner
            style={{
              minHeight: "140px",
              ...(isPdf ? { background: "#f8f8f9" } : {}),
            }}
          >
            {isPdf ? (
              <PdfIconWrap>
                <TextSnippetIcon
                  sx={{ fontSize: "2.5rem", color: "#e13b30" }}
                />
              </PdfIconWrap>
            ) : (
              <img src={imgSrc(value)} alt={label} />
            )}
            <DeleteBtn onClick={onDelete}>
              <Delete />
            </DeleteBtn>
          </ImageItemInner>
        </>
      ) : (
        <DropZone onDrop={handleDrop} accept=".jpg, .jpeg, .png, .webp, .pdf">
          {(props) => (
            <ImageItemInner style={{ minHeight: "140px" }}>
              <UploadPlaceholder {...props}>
                <PhotoCameraOutlined />
                <span>อัพโหลดรูปภาพ / PDF</span>
                <FileTip>.jpg .jpeg .png .webp .pdf • ไม่เกิน 4 MB</FileTip>
              </UploadPlaceholder>
            </ImageItemInner>
          )}
        </DropZone>
      )}
      <ImageMeta>
        <ImageMetaLabel>{label}</ImageMetaLabel>
        <ImageMetaDesc>{description}</ImageMetaDesc>
      </ImageMeta>
    </ImageItem>
  );
};

const MixedViewCard = ({ value, label, description, onView, onDownload }) => {
  const hasValue = checkNotEmpty(value);
  const isPdf = isPdfValue(value);

  const handlePdfDownload = () => {
    if (typeof value === "string") {
      downloadImage(transformPdfUrl(value));
    } else if (onDownload) {
      onDownload();
    }
  };

  return (
    <ImageItem>
      <ImageItemInner
        style={
          !hasValue || isPdf
            ? { background: "#f8f8f9", minHeight: "140px" }
            : { minHeight: "140px" }
        }
      >
        {hasValue ? (
          isPdf ? (
            <PdfIconWrap>
              <TextSnippetIcon sx={{ fontSize: "2.5rem", color: "#e13b30" }} />
              <DownloadPdfLink onClick={handlePdfDownload}>
                Download PDF
              </DownloadPdfLink>
            </PdfIconWrap>
          ) : (
            <img
              src={typeof value === "string" ? value : imgSrc(value)}
              alt={label}
            />
          )
        ) : (
          <InsertPhotoIcon sx={{ color: "#d8d8dc", fontSize: "2.5rem" }} />
        )}
      </ImageItemInner>
      {hasValue && !isPdf && onView && typeof value === "string" && (
        <ViewFullLink onClick={onView}>
          คลิกเพิ่มเติมเพื่อดูรูปเต็ม
        </ViewFullLink>
      )}
      <ImageMeta>
        <ImageMetaLabel>{label}</ImageMetaLabel>
        <ImageMetaDesc>{description}</ImageMetaDesc>
      </ImageMeta>
    </ImageItem>
  );
};

/* ─── Main Component ──────────────────────────────────────── */

const Profile = ({ open }) => {
  const { openDialog, closeDialog, closeAllDialogs } = useDialog();
  const { enqueueSnackbar } = useSnackbar();

  const [profile, setProfile] = useState({});
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    fetchProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  const fetchProfileData = async () => {
    try {
      const { data } = await getSellerProfile();
      const decrypt = await decryptData(data);
      setProfile({
        ...decrypt,
        _id: decrypt?.seller_id || "",
        icon_homepage: decrypt?.icon_homepage || "",
        profile_image: decrypt?.profile_image || "",
        warehouse_image: decrypt?.warehouse_image || "",
        registation: decrypt?.registation || "",
        banner_image: decrypt?.banner_image || "",
        id_card_image: decrypt?.id_card_image || "",
        book_bank_image: decrypt?.book_bank_image || "",
        work_image_first: decrypt?.work_image_first || "",
        work_image_second: decrypt?.work_image_second || "",
        work_image_third: decrypt?.work_image_third || "",
        work_image_fourth: decrypt?.work_image_fourth || "",
        work_image_fifth: decrypt?.work_image_fifth || "",
        work_image_sixth: decrypt?.work_image_sixth || "",
      });
    } catch (err) {
      console.error("err:", err);
    }
  };

  const handleUpdate = () => {
    const data = new FormData();
    Object.keys(profile).map((key) => {
      data.append(key, profile[key]);
      return data;
    });

    updateSellerProfile(data)
      .then((response) => {
        if (response.data === undefined) return;
        enqueueSnackbar(`แก้ไขสำเร็จ`, { variant: "success" });
        setIsEdit(false);
      })
      .catch((error) => {
        if (error.response?.data === undefined) return;
        enqueueSnackbar(`แก้ไขไม่สำเร็จ`, { variant: "error" });
        setIsEdit(false);
      });
  };

  const PartnerType = (type) => {
    if (type === 1) return getLocale() === "th" ? "โรงงาน" : "Manufactory";
    if (type === 2) return getLocale() === "th" ? "เจ้าของร้าน" : "Store owner";
    if (type === 3)
      return getLocale() === "th" ? "ผู้จัดจำหน่าย" : "Distributor";
    if (type === 4) return getLocale() === "th" ? "ร้านค้าปลีก" : "Retailer";
    return getLocale() === "th" ? "ตัวแทนขาย" : "Sale representative";
  };

  const displayValue = (val) => (!val || val === "null" ? "-" : val);

  const openFullImage = (src) => {
    openDialog({
      type: "custom",
      customDialog: <ModalFullImage image={src} close={() => closeDialog()} />,
    });
  };

  const th = getLocale() === "th";
  return (
    <Main
      sx={{ marginTop: "75px", background: "#f7f7f8", width: "100%" }}
      open={open}
    >
      <PageContainer>
        {/* ── Page Header ───────────────────────────────── */}
        <PageHeader>
          <PageTitle>{th ? "โปรไฟล์ของผู้ขาย" : "Seller Profile"}</PageTitle>

          <HeaderActions>
            {isEdit ? (
              <>
                <StyledButton
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  onClick={() =>
                    openDialog({
                      type: "confirm",
                      title: "คุณต้องการยกเลิกการแก้ไขใช่หรือไม่",
                      onConfirm: () => {
                        setIsEdit(false);
                        closeDialog();
                      },
                      maxWidth: 342,
                    })
                  }
                >
                  {th ? "ยกเลิก" : "Cancel"}
                </StyledButton>
                <StyledButton
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() =>
                    openDialog({
                      type: "confirm",
                      title: "คุณต้องการบันทึกการแก้ไขใช่หรือไม่",
                      onConfirm: () => {
                        handleUpdate();
                        closeDialog();
                      },
                      maxWidth: 342,
                    })
                  }
                >
                  {th ? "บันทึก" : "Save"}
                </StyledButton>
              </>
            ) : (
              <StyledButton
                variant="secondary"
                startIcon={<EditIcon />}
                onClick={() => setIsEdit(true)}
              >
                {th ? "แก้ไขข้อมูล" : "Edit profile"}
              </StyledButton>
            )}
          </HeaderActions>
        </PageHeader>

        {/* ── Top Section: Info + Stats ─────────────────── */}
        <Grid container spacing={3}>
          {/* Info Card */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardTitle>
                {th ? "ข้อมูลบัญชี" : "Account Information"}
              </CardTitle>

              {isEdit ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FieldLabel>
                      {th ? "ชื่อบริษัท" : "Company name"}
                    </FieldLabel>
                    <WrapperTextfield>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={profile?.company_name || ""}
                        onChange={(e) =>
                          setProfile({
                            ...profile,
                            company_name: e.target.value,
                          })
                        }
                        error={!checkNotEmpty(profile?.company_name)}
                        helperText={
                          !checkNotEmpty(profile?.company_name)
                            ? "โปรดระบุชื่อบริษัท"
                            : "ชื่อบริษัทหรือชื่อร้านค้าที่ปรากฏในระบบ"
                        }
                      />
                    </WrapperTextfield>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FieldLabel>
                      {th ? "เบอร์โทรศัพท์" : "Phone number"}
                    </FieldLabel>
                    <WrapperTextfield>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={profile?.phone_number || ""}
                        onChange={(e) =>
                          setProfile({
                            ...profile,
                            phone_number: e.target.value,
                          })
                        }
                        error={!checkNotEmpty(profile?.phone_number)}
                        helperText={
                          !checkNotEmpty(profile?.phone_number)
                            ? "โปรดระบุเบอร์โทรศัพท์"
                            : "เบอร์ติดต่อ เช่น 02-xxx-xxxx หรือ 08x-xxx-xxxx"
                        }
                      />
                    </WrapperTextfield>
                  </Grid>

                  <Grid item xs={12}>
                    <FieldLabel>{th ? "อีเมล" : "Email address"}</FieldLabel>
                    <FieldValue style={{ color: "#6c6c70" }}>
                      {displayValue(profile?.email)}
                    </FieldValue>
                  </Grid>

                  <Grid item xs={12}>
                    <FieldLabel>
                      {th ? "ที่อยู่บริษัท" : "Company address"}
                    </FieldLabel>
                    <WrapperTextfield>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={profile?.address || ""}
                        onChange={(e) =>
                          setProfile({ ...profile, address: e.target.value })
                        }
                        error={!checkNotEmpty(profile?.address)}
                        helperText={
                          !checkNotEmpty(profile?.address)
                            ? "โปรดระบุที่อยู่บริษัท"
                            : "ที่อยู่บริษัทหรือสถานประกอบการ"
                        }
                      />
                    </WrapperTextfield>
                  </Grid>

                  <Grid item xs={12}>
                    <FieldLabel>
                      {th ? "เกี่ยวกับบริษัท" : "About company"}
                    </FieldLabel>
                    <WrapperTextfield>
                      <TextField
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={4}
                        value={profile?.description || ""}
                        onChange={(e) =>
                          setProfile({
                            ...profile,
                            description: e.target.value,
                          })
                        }
                        error={!checkNotEmpty(profile?.description)}
                        helperText={
                          !checkNotEmpty(profile?.description)
                            ? "โปรดระบุเกี่ยวกับบริษัท"
                            : "แนะนำบริษัท สินค้า หรือบริการของคุณโดยย่อ"
                        }
                      />
                    </WrapperTextfield>
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FieldGroup>
                      <FieldLabel>
                        {th ? "ชื่อบริษัท" : "Company name"}
                      </FieldLabel>
                      <FieldValue>
                        {displayValue(profile?.company_name)}
                      </FieldValue>
                    </FieldGroup>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FieldGroup>
                      <FieldLabel>
                        {th ? "เบอร์โทรศัพท์" : "Phone number"}
                      </FieldLabel>
                      <FieldValue>
                        {displayValue(profile?.phone_number)}
                      </FieldValue>
                    </FieldGroup>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FieldGroup>
                      <FieldLabel>{th ? "อีเมล" : "Email address"}</FieldLabel>
                      <FieldValue>{displayValue(profile?.email)}</FieldValue>
                    </FieldGroup>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FieldGroup>
                      <FieldLabel>
                        {th ? "ที่อยู่บริษัท" : "Company address"}
                      </FieldLabel>
                      <FieldValue>{displayValue(profile?.address)}</FieldValue>
                    </FieldGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <FieldGroup>
                      <FieldLabel>
                        {th ? "เกี่ยวกับบริษัท" : "About company"}
                      </FieldLabel>
                      <FieldValueDesc>
                        {displayValue(profile?.description)}
                      </FieldValueDesc>
                    </FieldGroup>
                  </Grid>
                </Grid>
              )}

              <Divider sx={{ my: 2 }} />

              <FieldLabel sx={{ mb: 1 }}>
                {th ? "ตำแหน่งที่ตั้งบริษัท" : "Company location"}
              </FieldLabel>
              <StyledButton
                variant="secondary"
                startIcon={<LocationOnIcon />}
                onClick={() =>
                  openDialog({
                    type: "custom",
                    customDialog: (
                      <AddLocationDialog
                        sellerDetail={profile}
                        onClose={() => closeDialog()}
                        onSuccess={() => {
                          closeAllDialogs();
                          fetchProfileData();
                        }}
                      />
                    ),
                  })
                }
              >
                {th ? "จัดการตำแหน่งที่ตั้งบริษัท" : "Manage company location"}
              </StyledButton>
            </Card>
          </Grid>

          {/* Stats Cards */}
          <Grid item xs={12} md={4}>
            <StatCard>
              <StatIconWrap>
                <Inventory2OutlinedIcon
                  sx={{ color: "#e13b30", fontSize: "1.35rem" }}
                />
              </StatIconWrap>
              <div>
                <StatLabel>{th ? "จำนวนสินค้า" : "Products"}</StatLabel>
                <StatValue>{profile?.productCount ?? "-"}</StatValue>
              </div>
            </StatCard>

            <StatCard>
              <StatIconWrap>
                <CalendarTodayOutlinedIcon
                  sx={{ color: "#e13b30", fontSize: "1.35rem" }}
                />
              </StatIconWrap>
              <div>
                <StatLabel>{th ? "วันที่เข้าร่วม" : "Join date"}</StatLabel>
                <StatValue>
                  {profile?.createdAt
                    ? moment(profile.createdAt).format("L")
                    : "-"}
                </StatValue>
              </div>
            </StatCard>

            <StatCard>
              <StatIconWrap>
                <BusinessCenterOutlinedIcon
                  sx={{ color: "#e13b30", fontSize: "1.35rem" }}
                />
              </StatIconWrap>
              <div>
                <StatLabel>{th ? "ประเภทธุรกิจ" : "Business type"}</StatLabel>
                <StatValue>{PartnerType(profile?.seller_type)}</StatValue>
              </div>
            </StatCard>
          </Grid>
        </Grid>

        {/* ── Documents & Profile Images ─────────────────── */}
        <Card>
          <CardTitle>{th ? "รูปภาพและเอกสาร" : "Images & Documents"}</CardTitle>

          <ImageGrid>
            {/* HIDDEN: icon_homepage
            <ImageViewCard
              value={profile?.icon_homepage}
              label={th ? "ไอคอนหน้าเว็บ" : "Homepage icon"}
              description={
                th ? "รูปภาพไอค่อนหน้าเว็บไซต์" : "Image of icon homepage"
              }
              onView={() => openFullImage(profile.icon_homepage)}
            />
            */}

            {/* Profile Image */}
            {isEdit ? (
              <ImageUploadCard
                value={profile?.profile_image}
                onDrop={(files) =>
                  setProfile({ ...profile, profile_image: files[0] })
                }
                onDelete={() => setProfile({ ...profile, profile_image: "" })}
                accept=".jpg, .jpeg, .png, .webp"
                label={th ? "โลโก้ร้านค้า" : "Store / Logo"}
                description={
                  th ? "รูปภาพโลโก้ร้านค้า" : "Image of store or logo"
                }
              />
            ) : (
              <ImageViewCard
                value={profile?.profile_image}
                label={th ? "โลโก้ร้านค้า" : "Store / Logo"}
                description={
                  th ? "รูปภาพโลโก้ร้านค้า" : "Image of store or logo"
                }
                onView={() => openFullImage(profile.profile_image)}
              />
            )}

            {/* ID Card */}
            {isEdit ? (
              <MixedUploadCard
                value={profile?.id_card_image}
                onDrop={(files) =>
                  setProfile({ ...profile, id_card_image: files[0] })
                }
                onDelete={() => setProfile({ ...profile, id_card_image: "" })}
                label={th ? "สำเนาบัตร/ใบทะเบียน" : "ID / Registration"}
                description={
                  th
                    ? "สำเนาใบทะเบียนภาษีมูลค่าเพิ่ม (ภ.พ.20) หรือสำเนาบัตรประชาชน"
                    : "A copy of company registration or ID card"
                }
                onSizeError={() =>
                  enqueueSnackbar("ไฟล์ต้องมีขนาดไม่เกิน 4 MB", {
                    variant: "error",
                  })
                }
              />
            ) : (
              <MixedViewCard
                value={profile?.id_card_image}
                label={th ? "สำเนาบัตร/ใบทะเบียน" : "ID / Registration"}
                description={
                  th
                    ? "สำเนาใบทะเบียนภาษีมูลค่าเพิ่ม (ภ.พ.20) หรือสำเนาบัตรประชาชน"
                    : "A copy of company registration or ID card"
                }
                onView={() => openFullImage(profile.id_card_image)}
                onDownload={() => downloadImage(profile.id_card_image)}
              />
            )}

            {/* Warehouse */}
            {isEdit ? (
              <MixedUploadCard
                value={profile?.warehouse_image}
                onDrop={(files) =>
                  setProfile({ ...profile, warehouse_image: files[0] })
                }
                onDelete={() => setProfile({ ...profile, warehouse_image: "" })}
                label={th ? "ภาพหน้าร้าน/โรงงาน" : "Store / Factory front"}
                description={
                  th
                    ? "รูปภาพหน้าร้านค้าหรือภาพสินค้าที่เกี่ยวข้องเพื่อยืนยันตัวตน"
                    : "A photo of company, factory or store front in order to verify"
                }
                onSizeError={() =>
                  enqueueSnackbar("ไฟล์ต้องมีขนาดไม่เกิน 4 MB", {
                    variant: "error",
                  })
                }
              />
            ) : (
              <MixedViewCard
                value={profile?.warehouse_image}
                label={th ? "ภาพหน้าร้าน/โรงงาน" : "Store / Factory front"}
                description={
                  th
                    ? "รูปภาพหน้าร้านค้าหรือภาพสินค้าที่เกี่ยวข้องเพื่อยืนยันตัวตน"
                    : "A photo of company, factory or store front in order to verify"
                }
                onView={() => openFullImage(profile.warehouse_image)}
                onDownload={() => downloadImage(profile.warehouse_image)}
              />
            )}

            {/* Book bank */}
            {isEdit ? (
              <MixedUploadCard
                value={profile?.book_bank_image}
                onDrop={(files) =>
                  setProfile({ ...profile, book_bank_image: files[0] })
                }
                onDelete={() => setProfile({ ...profile, book_bank_image: "" })}
                label={th ? "สมุดบัญชีธนาคาร" : "Bank book"}
                description={th ? "รูปภาพสมุดบัญชี" : "Image of book bank"}
                onSizeError={() =>
                  enqueueSnackbar("ไฟล์ต้องมีขนาดไม่เกิน 4 MB", {
                    variant: "error",
                  })
                }
              />
            ) : (
              <MixedViewCard
                value={profile?.book_bank_image}
                label={th ? "สมุดบัญชีธนาคาร" : "Bank book"}
                description={th ? "รูปภาพสมุดบัญชี" : "Image of book bank"}
                onView={() => openFullImage(profile.book_bank_image)}
                onDownload={() => downloadImage(profile.book_bank_image)}
              />
            )}

            {/* Registration PDF */}
            {isEdit ? (
              <MixedUploadCard
                value={profile?.registation}
                onDrop={(files) =>
                  setProfile({ ...profile, registation: files[0] })
                }
                onDelete={() => setProfile({ ...profile, registation: "" })}
                label={th ? "เอกสารร้านค้า" : "Store documents"}
                description={
                  th
                    ? "เอกสารสำเนาเกี่ยวข้องกับร้านค้า"
                    : "Copy of documents related to the store"
                }
                onSizeError={() =>
                  enqueueSnackbar("ไฟล์ต้องมีขนาดไม่เกิน 4 MB", {
                    variant: "error",
                  })
                }
              />
            ) : (
              <MixedViewCard
                value={profile?.registation}
                label={th ? "เอกสารร้านค้า" : "Store documents"}
                description={
                  th
                    ? "เอกสารสำเนาเกี่ยวข้องกับร้านค้า"
                    : "Copy of documents related to the store"
                }
                onDownload={() => downloadImage(profile.registation)}
              />
            )}
          </ImageGrid>
        </Card>

        {/* ── Gallery Images ─────────────────────────────── */}
        <Card>
          <CardTitle>{th ? "รูปภาพแกลเลอรี่" : "Gallery Images"}</CardTitle>

          <Grid container spacing={2}>
            {/* Banner */}
            <Grid item xs={12} sm={6}>
              {isEdit ? (
                <ImageItem>
                  {checkNotEmpty(profile?.banner_image) ? (
                    <>
                      <ImageItemWideInner>
                        <img src={imgSrc(profile.banner_image)} alt="banner" />
                        <DeleteBtn
                          onClick={() =>
                            setProfile({ ...profile, banner_image: "" })
                          }
                        >
                          <Delete />
                        </DeleteBtn>
                      </ImageItemWideInner>
                    </>
                  ) : (
                    <DropZone
                      onDrop={(files) =>
                        setProfile({ ...profile, banner_image: files[0] })
                      }
                      accept=".jpg, .jpeg, .png, .webp"
                    >
                      {(props) => (
                        <ImageItemWideInner>
                          <UploadPlaceholder {...props}>
                            <PhotoCameraOutlined />
                            <span>อัพโหลดรูปภาพ</span>
                            <FileTip>.jpg .jpeg .png .webp</FileTip>
                          </UploadPlaceholder>
                        </ImageItemWideInner>
                      )}
                    </DropZone>
                  )}
                  <ImageMeta>
                    <ImageMetaLabel>
                      {th ? "แบนเนอร์" : "Banner"}
                    </ImageMetaLabel>
                  </ImageMeta>
                </ImageItem>
              ) : (
                <ImageItem>
                  <ImageItemWideInner
                    style={
                      !checkNotEmpty(profile?.banner_image)
                        ? { background: "#f8f8f9" }
                        : {}
                    }
                  >
                    {checkNotEmpty(profile?.banner_image) ? (
                      <img src={profile.banner_image} alt="banner" />
                    ) : (
                      <InsertPhotoIcon
                        sx={{ color: "#d8d8dc", fontSize: "2.5rem" }}
                      />
                    )}
                  </ImageItemWideInner>
                  <ImageMeta>
                    <ImageMetaLabel>
                      {th ? "แบนเนอร์" : "Banner"}
                    </ImageMetaLabel>
                  </ImageMeta>
                </ImageItem>
              )}
            </Grid>

            {/* work_image_first */}
            <Grid item xs={12} sm={6}>
              {isEdit ? (
                <ImageItem>
                  {checkNotEmpty(profile?.work_image_first) ? (
                    <>
                      <ImageItemWideInner>
                        <img
                          src={imgSrc(profile.work_image_first)}
                          alt="work 1"
                        />
                        <DeleteBtn
                          onClick={() =>
                            setProfile({ ...profile, work_image_first: "" })
                          }
                        >
                          <Delete />
                        </DeleteBtn>
                      </ImageItemWideInner>
                    </>
                  ) : (
                    <DropZone
                      onDrop={(files) =>
                        setProfile({ ...profile, work_image_first: files[0] })
                      }
                      accept=".jpg, .jpeg, .png, .webp"
                    >
                      {(props) => (
                        <ImageItemWideInner>
                          <UploadPlaceholder {...props}>
                            <PhotoCameraOutlined />
                            <span>อัพโหลดรูปภาพ</span>
                            <FileTip>.jpg .jpeg .png .webp</FileTip>
                          </UploadPlaceholder>
                        </ImageItemWideInner>
                      )}
                    </DropZone>
                  )}
                  <ImageMeta>
                    <ImageMetaLabel>
                      {th ? "ภาพผลงาน 1" : "Work image 1"}
                    </ImageMetaLabel>
                  </ImageMeta>
                </ImageItem>
              ) : (
                <ImageItem>
                  <ImageItemWideInner
                    style={
                      !checkNotEmpty(profile?.work_image_first)
                        ? { background: "#f8f8f9" }
                        : {}
                    }
                  >
                    {checkNotEmpty(profile?.work_image_first) ? (
                      <img src={profile.work_image_first} alt="work 1" />
                    ) : (
                      <InsertPhotoIcon
                        sx={{ color: "#d8d8dc", fontSize: "2.5rem" }}
                      />
                    )}
                  </ImageItemWideInner>
                  <ImageMeta>
                    <ImageMetaLabel>
                      {th ? "ภาพผลงาน 1" : "Work image 1"}
                    </ImageMetaLabel>
                  </ImageMeta>
                </ImageItem>
              )}
            </Grid>

            {/* work_image_second */}
            <Grid item xs={12} sm={6}>
              {isEdit ? (
                <ImageItem>
                  {checkNotEmpty(profile?.work_image_second) ? (
                    <>
                      <ImageItemWideInner>
                        <img
                          src={imgSrc(profile.work_image_second)}
                          alt="work 2"
                        />
                        <DeleteBtn
                          onClick={() =>
                            setProfile({ ...profile, work_image_second: "" })
                          }
                        >
                          <Delete />
                        </DeleteBtn>
                      </ImageItemWideInner>
                    </>
                  ) : (
                    <DropZone
                      onDrop={(files) =>
                        setProfile({ ...profile, work_image_second: files[0] })
                      }
                      accept=".jpg, .jpeg, .png, .webp"
                    >
                      {(props) => (
                        <ImageItemWideInner>
                          <UploadPlaceholder {...props}>
                            <PhotoCameraOutlined />
                            <span>อัพโหลดรูปภาพ</span>
                            <FileTip>.jpg .jpeg .png .webp</FileTip>
                          </UploadPlaceholder>
                        </ImageItemWideInner>
                      )}
                    </DropZone>
                  )}
                  <ImageMeta>
                    <ImageMetaLabel>
                      {th ? "ภาพผลงาน 2" : "Work image 2"}
                    </ImageMetaLabel>
                  </ImageMeta>
                </ImageItem>
              ) : (
                <ImageItem>
                  <ImageItemWideInner
                    style={
                      !checkNotEmpty(profile?.work_image_second)
                        ? { background: "#f8f8f9" }
                        : {}
                    }
                  >
                    {checkNotEmpty(profile?.work_image_second) ? (
                      <img src={profile.work_image_second} alt="work 2" />
                    ) : (
                      <InsertPhotoIcon
                        sx={{ color: "#d8d8dc", fontSize: "2.5rem" }}
                      />
                    )}
                  </ImageItemWideInner>
                  <ImageMeta>
                    <ImageMetaLabel>
                      {th ? "ภาพผลงาน 2" : "Work image 2"}
                    </ImageMetaLabel>
                  </ImageMeta>
                </ImageItem>
              )}
            </Grid>

            {/* work_image_third */}
            <Grid item xs={12} sm={6}>
              {isEdit ? (
                <ImageItem>
                  {checkNotEmpty(profile?.work_image_third) ? (
                    <>
                      <ImageItemWideInner>
                        <img
                          src={imgSrc(profile.work_image_third)}
                          alt="work 3"
                        />
                        <DeleteBtn
                          onClick={() =>
                            setProfile({ ...profile, work_image_third: "" })
                          }
                        >
                          <Delete />
                        </DeleteBtn>
                      </ImageItemWideInner>
                    </>
                  ) : (
                    <DropZone
                      onDrop={(files) =>
                        setProfile({ ...profile, work_image_third: files[0] })
                      }
                      accept=".jpg, .jpeg, .png, .webp"
                    >
                      {(props) => (
                        <ImageItemWideInner>
                          <UploadPlaceholder {...props}>
                            <PhotoCameraOutlined />
                            <span>อัพโหลดรูปภาพ</span>
                            <FileTip>.jpg .jpeg .png .webp</FileTip>
                          </UploadPlaceholder>
                        </ImageItemWideInner>
                      )}
                    </DropZone>
                  )}
                  <ImageMeta>
                    <ImageMetaLabel>
                      {th ? "ภาพผลงาน 3" : "Work image 3"}
                    </ImageMetaLabel>
                  </ImageMeta>
                </ImageItem>
              ) : (
                <ImageItem>
                  <ImageItemWideInner
                    style={
                      !checkNotEmpty(profile?.work_image_third)
                        ? { background: "#f8f8f9" }
                        : {}
                    }
                  >
                    {checkNotEmpty(profile?.work_image_third) ? (
                      <img src={profile.work_image_third} alt="work 3" />
                    ) : (
                      <InsertPhotoIcon
                        sx={{ color: "#d8d8dc", fontSize: "2.5rem" }}
                      />
                    )}
                  </ImageItemWideInner>
                  <ImageMeta>
                    <ImageMetaLabel>
                      {th ? "ภาพผลงาน 3" : "Work image 3"}
                    </ImageMetaLabel>
                  </ImageMeta>
                </ImageItem>
              )}
            </Grid>

            {/* work_image_fourth */}
            <Grid item xs={12} sm={4}>
              {isEdit ? (
                <ImageItem>
                  {checkNotEmpty(profile?.work_image_fourth) ? (
                    <>
                      <ImageItemWideInner>
                        <img
                          src={imgSrc(profile.work_image_fourth)}
                          alt="work 4"
                        />
                        <DeleteBtn
                          onClick={() =>
                            setProfile({ ...profile, work_image_fourth: "" })
                          }
                        >
                          <Delete />
                        </DeleteBtn>
                      </ImageItemWideInner>
                    </>
                  ) : (
                    <DropZone
                      onDrop={(files) =>
                        setProfile({ ...profile, work_image_fourth: files[0] })
                      }
                      accept=".jpg, .jpeg, .png, .webp"
                    >
                      {(props) => (
                        <ImageItemWideInner>
                          <UploadPlaceholder {...props}>
                            <PhotoCameraOutlined />
                            <span>อัพโหลดรูปภาพ</span>
                            <FileTip>.jpg .jpeg .png .webp</FileTip>
                          </UploadPlaceholder>
                        </ImageItemWideInner>
                      )}
                    </DropZone>
                  )}
                  <ImageMeta>
                    <ImageMetaLabel>
                      {th ? "ภาพผลงาน 4" : "Work image 4"}
                    </ImageMetaLabel>
                  </ImageMeta>
                </ImageItem>
              ) : (
                <ImageItem>
                  <ImageItemWideInner
                    style={
                      !checkNotEmpty(profile?.work_image_fourth)
                        ? { background: "#f8f8f9" }
                        : {}
                    }
                  >
                    {checkNotEmpty(profile?.work_image_fourth) ? (
                      <img src={profile.work_image_fourth} alt="work 4" />
                    ) : (
                      <InsertPhotoIcon
                        sx={{ color: "#d8d8dc", fontSize: "2.5rem" }}
                      />
                    )}
                  </ImageItemWideInner>
                  <ImageMeta>
                    <ImageMetaLabel>
                      {th ? "ภาพผลงาน 4" : "Work image 4"}
                    </ImageMetaLabel>
                  </ImageMeta>
                </ImageItem>
              )}
            </Grid>

            {/* work_image_fifth */}
            <Grid item xs={12} sm={4}>
              {isEdit ? (
                <ImageItem>
                  {checkNotEmpty(profile?.work_image_fifth) ? (
                    <>
                      <ImageItemWideInner>
                        <img
                          src={imgSrc(profile.work_image_fifth)}
                          alt="work 5"
                        />
                        <DeleteBtn
                          onClick={() =>
                            setProfile({ ...profile, work_image_fifth: "" })
                          }
                        >
                          <Delete />
                        </DeleteBtn>
                      </ImageItemWideInner>
                    </>
                  ) : (
                    <DropZone
                      onDrop={(files) =>
                        setProfile({ ...profile, work_image_fifth: files[0] })
                      }
                      accept=".jpg, .jpeg, .png, .webp"
                    >
                      {(props) => (
                        <ImageItemWideInner>
                          <UploadPlaceholder {...props}>
                            <PhotoCameraOutlined />
                            <span>อัพโหลดรูปภาพ</span>
                            <FileTip>.jpg .jpeg .png .webp</FileTip>
                          </UploadPlaceholder>
                        </ImageItemWideInner>
                      )}
                    </DropZone>
                  )}
                  <ImageMeta>
                    <ImageMetaLabel>
                      {th ? "ภาพผลงาน 5" : "Work image 5"}
                    </ImageMetaLabel>
                  </ImageMeta>
                </ImageItem>
              ) : (
                <ImageItem>
                  <ImageItemWideInner
                    style={
                      !checkNotEmpty(profile?.work_image_fifth)
                        ? { background: "#f8f8f9" }
                        : {}
                    }
                  >
                    {checkNotEmpty(profile?.work_image_fifth) ? (
                      <img src={profile.work_image_fifth} alt="work 5" />
                    ) : (
                      <InsertPhotoIcon
                        sx={{ color: "#d8d8dc", fontSize: "2.5rem" }}
                      />
                    )}
                  </ImageItemWideInner>
                  <ImageMeta>
                    <ImageMetaLabel>
                      {th ? "ภาพผลงาน 5" : "Work image 5"}
                    </ImageMetaLabel>
                  </ImageMeta>
                </ImageItem>
              )}
            </Grid>

            {/* work_image_sixth */}
            <Grid item xs={12} sm={4}>
              {isEdit ? (
                <ImageItem>
                  {checkNotEmpty(profile?.work_image_sixth) ? (
                    <>
                      <ImageItemWideInner>
                        <img
                          src={imgSrc(profile.work_image_sixth)}
                          alt="work 6"
                        />
                        <DeleteBtn
                          onClick={() =>
                            setProfile({ ...profile, work_image_sixth: "" })
                          }
                        >
                          <Delete />
                        </DeleteBtn>
                      </ImageItemWideInner>
                    </>
                  ) : (
                    <DropZone
                      onDrop={(files) =>
                        setProfile({ ...profile, work_image_sixth: files[0] })
                      }
                      accept=".jpg, .jpeg, .png, .webp"
                    >
                      {(props) => (
                        <ImageItemWideInner>
                          <UploadPlaceholder {...props}>
                            <PhotoCameraOutlined />
                            <span>อัพโหลดรูปภาพ</span>
                            <FileTip>.jpg .jpeg .png .webp</FileTip>
                          </UploadPlaceholder>
                        </ImageItemWideInner>
                      )}
                    </DropZone>
                  )}
                  <ImageMeta>
                    <ImageMetaLabel>
                      {th ? "ภาพผลงาน 6" : "Work image 6"}
                    </ImageMetaLabel>
                  </ImageMeta>
                </ImageItem>
              ) : (
                <ImageItem>
                  <ImageItemWideInner
                    style={
                      !checkNotEmpty(profile?.work_image_sixth)
                        ? { background: "#f8f8f9" }
                        : {}
                    }
                  >
                    {checkNotEmpty(profile?.work_image_sixth) ? (
                      <img src={profile.work_image_sixth} alt="work 6" />
                    ) : (
                      <InsertPhotoIcon
                        sx={{ color: "#d8d8dc", fontSize: "2.5rem" }}
                      />
                    )}
                  </ImageItemWideInner>
                  <ImageMeta>
                    <ImageMetaLabel>
                      {th ? "ภาพผลงาน 6" : "Work image 6"}
                    </ImageMetaLabel>
                  </ImageMeta>
                </ImageItem>
              )}
            </Grid>
          </Grid>
        </Card>
      </PageContainer>
    </Main>
  );
};

export default Profile;
